import {
  META_DESCRIPTION_WARNING_LENGTH,
  normalizeMetaDescription,
} from "@portal/app/_common/lib/metaDescription";

type LexicalTextNode = {
  type: "text";
  text: string;
  detail: number;
  format: number;
  mode: "normal";
  style: string;
  version: number;
};

type LexicalElementNode = {
  type: string;
  children?: LexicalNode[];
  direction?: "ltr" | "rtl" | null;
  format?: "" | "left" | "start" | "center" | "right" | "end" | "justify";
  indent?: number;
  textFormat?: number;
  textStyle?: string;
  tag?: string;
  value?: number;
  version: number;
  [key: string]: unknown;
};

type LexicalNode = LexicalTextNode | LexicalElementNode;

export type BlogRichText = {
  root: {
    type: "root";
    children: LexicalNode[];
    direction: "ltr" | "rtl" | null;
    format: "" | "left" | "start" | "center" | "right" | "end" | "justify";
    indent: number;
    version: number;
  };
};

const TRAILING_TRUNCATION_ARTIFACTS = /[\s.,;:!?-]+$/;

export const createBlogRichText = (children: LexicalNode[]): BlogRichText => ({
  root: {
    type: "root",
    children,
    direction: "ltr",
    format: "",
    indent: 0,
    version: 1,
  },
});

function isLexicalElementNode(node: LexicalNode | undefined): node is LexicalElementNode {
  return !!node && typeof node === "object" && "type" in node && node.type !== "text";
}

function isLexicalTextNode(node: LexicalNode): node is LexicalTextNode {
  return node.type === "text" && typeof (node as LexicalTextNode).text === "string";
}

function getNodeText(node: LexicalNode): string {
  if (isLexicalTextNode(node)) {
    return node.text;
  }
  return isLexicalElementNode(node) ? (node.children ?? []).map(getNodeText).join("") : "";
}

export function getBlogRichTextPlainText(content: BlogRichText | null | undefined): string {
  return (content?.root.children ?? [])
    .map(getNodeText)
    .map((text) => text.trim())
    .filter(Boolean)
    .join("\n\n");
}

export function splitBlogRichTextContent(
  content: BlogRichText
): [BlogRichText, BlogRichText, BlogRichText] {
  const children = content.root.children;
  const sections: LexicalNode[][] = [];

  for (const child of children) {
    if (isLexicalElementNode(child) && child.type === "heading") {
      sections.push([child]);
      continue;
    }

    if (sections.length === 0) {
      sections.push([child]);
    } else {
      sections[sections.length - 1].push(child);
    }
  }

  if (sections.length <= 1) {
    const paragraphSplit = splitParagraphFallback(children);
    return paragraphSplit.map(createBlogRichText) as [BlogRichText, BlogRichText, BlogRichText];
  }

  if (sections.length <= 2) {
    return [createBlogRichText([]), content, createBlogRichText([])];
  }

  if (sections.length <= 4) {
    return [
      createBlogRichText(sections[0]),
      createBlogRichText(sections.slice(1).flat()),
      createBlogRichText([]),
    ];
  }

  const middleBottomSplitIdx = Math.floor((sections.length - 1) / 2) + 1;
  return [
    createBlogRichText(sections[0]),
    createBlogRichText(sections.slice(1, middleBottomSplitIdx + 1).flat()),
    createBlogRichText(sections.slice(middleBottomSplitIdx + 1).flat()),
  ];
}

function splitParagraphFallback(
  children: LexicalNode[]
): [LexicalNode[], LexicalNode[], LexicalNode[]] {
  if (children.length <= 4) {
    return [[], children, []];
  }

  const middleSplitIdx = Math.floor(children.length / 2);
  return [children.slice(0, middleSplitIdx), children.slice(middleSplitIdx), []];
}

export function extractFAQsFromBlogRichText(
  content: BlogRichText | null | undefined
): { question: string; answer: string }[] {
  const children = content?.root.children ?? [];
  const faqs: { question: string; answer: string }[] = [];

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (!isLexicalElementNode(child) || child.type !== "heading") continue;

    const question = getNodeText(child).trim();
    if (!question.endsWith("?")) continue;
    if (/^frequently asked questions$/i.test(question)) continue;

    const answerNodes: LexicalNode[] = [];
    for (let j = i + 1; j < children.length; j++) {
      const next = children[j];
      if (isLexicalElementNode(next) && next.type === "heading") break;
      answerNodes.push(next);
    }

    const answer = answerNodes
      .map(getNodeText)
      .map((text) => text.trim())
      .filter(Boolean)
      .join("\n")
      .trim();

    if (answer) {
      faqs.push({ question, answer });
    }
  }

  return faqs;
}

export function truncateGeneratedBlogMetaDescription(description: string): string {
  if (description.length <= META_DESCRIPTION_WARNING_LENGTH) {
    return description;
  }

  const candidate = description.slice(0, META_DESCRIPTION_WARNING_LENGTH);
  const wordBoundaryIndex = candidate.search(/\s+\S*$/);
  const truncated = wordBoundaryIndex > 0 ? candidate.slice(0, wordBoundaryIndex) : candidate;

  return `${truncated.replace(TRAILING_TRUNCATION_ARTIFACTS, "")}...`;
}

export function getGeneratedBlogPostMetaDescription(content: BlogRichText): string {
  const [contentTop, contentMiddle, contentEnd] = splitBlogRichTextContent(content);
  const description =
    getBlogRichTextPlainText(contentTop) ||
    getBlogRichTextPlainText(contentMiddle) ||
    getBlogRichTextPlainText(contentEnd);
  return truncateGeneratedBlogMetaDescription(description.trim());
}

export function getLiveBlogPostMetaDescription({
  content,
  metaDescription,
}: {
  content: BlogRichText;
  metaDescription?: string | null;
}): string {
  return normalizeMetaDescription(metaDescription) ?? getGeneratedBlogPostMetaDescription(content);
}
